import { useSyncExternalStore } from 'react';
import { normalizeHost } from './normalize-host';
import { httpScanAdapter } from '@/lib/vscanner/http-scan-adapter';
import { ScanError } from '@/lib/vscanner/scan-adapter';
import { saveTicket } from '@/lib/vscanner/scan-ticket';
import type { ScanErrorCode, ScanState } from '@/lib/vscanner/types';

export const SCAN_TIMEOUT_MS = 90_000;

// Kept after the last banner unmounts so login <-> register (separate routes) does not
// restart a scan; dropped after this long without a banner on screen.
const IDLE_RESET_MS = 5 * 60_000;

const INITIAL: ScanState = {
  phase: 'idle',
  url: '',
  host: '',
  urlError: null,
  percent: 0,
  task: '',
  tasks: [],
  result: null,
  errorCode: null,
};

// Module-level store: the banner is mounted per page, the scan outlives it.
let state: ScanState = INITIAL;
let controller: AbortController | null = null;
let timeoutId: ReturnType<typeof setTimeout> | undefined;
let resetId: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function patch(next: Partial<ScanState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function stopRun() {
  clearTimeout(timeoutId);
  controller?.abort();
  controller = null;
}

function start() {
  if (state.phase === 'scanning') return;
  const checked = normalizeHost(state.url);
  if (checked.error) {
    patch({ urlError: checked.error });
    return;
  }
  const host = checked.host;
  const run = new AbortController();
  controller = run;
  patch({ phase: 'scanning', host, urlError: null, percent: 0, task: '', tasks: [], result: null, errorCode: null });

  timeoutId = setTimeout(() => {
    run.abort();
    patch({ phase: 'error', errorCode: 'TIMEOUT' });
  }, SCAN_TIMEOUT_MS);

  httpScanAdapter
    .run(
      host,
      ({ percent, task }) =>
        patch({ percent, task, tasks: state.tasks.at(-1) === task || !task ? state.tasks : [...state.tasks, task] }),
      run.signal,
    )
    .then((result) => {
      if (run.signal.aborted) return;
      clearTimeout(timeoutId);
      if (result.ref) saveTicket(result.ref.scanId, result.ref.token, host);
      patch({ phase: 'results', result });
    })
    .catch((err: unknown) => {
      if (run.signal.aborted) return;
      clearTimeout(timeoutId);
      const code: ScanErrorCode = err instanceof ScanError ? err.code : 'UNKNOWN';
      patch({ phase: 'error', errorCode: code });
    });
}

const actions = {
  setUrl: (url: string) => patch({ url, urlError: null }),
  start,
  retry: start,
  cancel: () => {
    stopRun();
    patch({ phase: 'idle' });
  },
  reset: () => {
    stopRun();
    state = INITIAL;
    listeners.forEach((l) => l());
  },
};

function subscribe(listener: () => void) {
  clearTimeout(resetId);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) resetId = setTimeout(actions.reset, IDLE_RESET_MS);
  };
}

const getSnapshot = () => state;

export function useScan() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...snapshot, ...actions };
}
